import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ComponentState,
  PageStateContext,
  PageStateContextValue,
  PageStateSnapshot,
} from './page-state-context'

export const PageStateProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [components, setComponents] = useState<PageStateSnapshot>({})

  // Keep a ref that always points to the latest components state. Used
  // by `getComponentState` / `getAllComponents` to return a fresh value
  // even when called from a stale-closure callback, and by `getSnapshot`
  // (the `useSyncExternalStore` accessor) to hand React a stable
  // reference between renders.
  const componentsRef = useRef(components)
  componentsRef.current = components

  // External-store subscribers driven by `usePageStateSelector`. We
  // keep a separate notify channel from React's setState so consumers
  // can subscribe to a slice without re-rendering on every full-context
  // mutation.
  const listenersRef = useRef<Set<() => void>>(new Set())

  // Fire subscribers after every commit where `components` changed.
  // `useEffect` runs post-commit, so listeners see the new snapshot
  // when they re-read via `getSnapshot`.
  useEffect(() => {
    listenersRef.current.forEach((listener) => {
      try {
        listener()
      } catch (err) {
        // Listener errors must not break the notify loop or other
        // subscribers — but silently swallowing them inside the
        // provider's effect means the consumer's own boundary never
        // sees them. Log so the listener bug is debuggable in dev
        // tools.
        // eslint-disable-next-line no-console
        console.error('PageStateProvider: listener threw', err)
      }
    })
  }, [components])

  const registerComponent = useCallback((state: ComponentState) => {
    setComponents((prev) => {
      const next = {
        ...prev,
        [state.componentId]: state,
      }
      return next
    })
  }, [])

  const unregisterComponent = useCallback((componentId: string) => {
    setComponents((prev) => {
      const next: Record<string, ComponentState> = {
        ...prev,
      }
      delete next[componentId]
      return next
    })
  }, [])

  const mergeComponentState = useCallback(
    (componentId: string, state: Partial<ComponentState['state']>) => {
      setComponents((prev) => {
        const next = { ...prev }
        const existing = next[componentId]
        if (existing) {
          next[componentId] = {
            ...existing,
            state: {
              ...existing.state,
              ...state,
            },
          }
        }
        return next
      })
    },
    [],
  )

  const setComponentState = useCallback(
    (componentId: string, state: ComponentState['state']) => {
      setComponents((prev) => {
        const next = { ...prev }
        next[componentId] = { ...next[componentId], state }
        return next
      })
    },
    [],
  )

  const getComponentState = useCallback((componentId: string) => {
    return componentsRef.current[componentId]
  }, [])

  const getAllComponents = useCallback(() => {
    return Object.values(componentsRef.current)
  }, [])

  /**
   * Notifications fire post-commit (after React's effect phase), not
   * synchronously with the mutation. A producer that calls
   * `mergeComponentState` and then synchronously reads via
   * `usePageStateSelector` in the same effect won't see the new value
   * until the next tick. Listeners can read the latest snapshot via
   * `getSnapshot()`.
   */
  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const getSnapshot = useCallback(() => componentsRef.current, [])

  const value: PageStateContextValue = {
    components,
    registerComponent,
    unregisterComponent,
    mergeComponentState,
    setComponentState,
    getComponentState,
    getAllComponents,
    subscribe,
    getSnapshot,
  }

  return (
    <PageStateContext.Provider value={value}>
      {children}
    </PageStateContext.Provider>
  )
}
